import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
  InputAdornment
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/AuthService';

interface PasswordResetRequestProps {
  onSuccess?: (email: string) => void;
  onCancel?: () => void;
}

const PasswordResetRequest: React.FC<PasswordResetRequestProps> = ({
  onSuccess,
  onCancel
}) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  
  const navigate = useNavigate();

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // Validate input
    const emailError = validateEmail(email);
    if (emailError) {
      setValidationErrors({ email: emailError });
      return;
    }
    
    setValidationErrors({});
    setLoading(true);
    setError('');

    try {
      const response = await AuthService.forgotPassword({ email });
      
      if (response.success) {
        setSuccess(true);
        if (onSuccess) {
          onSuccess(email);
        }
      } else {
        setError(response.message || 'Failed to send password reset email');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/login');
    }
  };

  if (success) {
    return (
      <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <EmailIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="success.main">
            Reset Link Sent
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            We've sent a password reset link to <strong>{email}</strong>. 
            Please check your email and follow the instructions to reset your password.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            If you don't see the email, check your spam folder. The link will expire in 2 hours.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<ArrowBackIcon />}
            >
              Back to Login
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent)}
              disabled={loading}
            >
              Resend Email
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Reset Password
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter your email address and we'll send you a link to reset your password.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          disabled={loading}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          autoFocus
        />

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={loading}
            startIcon={<ArrowBackIcon />}
          >
            Cancel
          </Button>
          
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !email}
            endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{ minWidth: 140 }}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </Box>
      </form>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Remember your password?{' '}
          <Link 
            component="button" 
            variant="body2" 
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
          >
            Sign in here
          </Link>
        </Typography>
      </Box>
    </Paper>
  );
};

export default PasswordResetRequest;